
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { PlaceholderInfoDirective } from 'src/app/shared/directives/placeholder-info.directive';
import { SubscriptionLike } from 'rxjs';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { CodemirrorHtmlComponent } from "../../../../../../../../shared/components/ai-bot/utils/codemirror-html/codemirror-html.component";
import { FusionService } from '@shared-angular/services/fusion.service';
import { getFusionBotTypeOptions } from '../fusionbot-tab.utils';
import { LanguageService } from '@shared-angular/services/language.service';
import { RouteShareService } from '../../../../../../../../shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';

@Component({
  selector: 'app-fusion-htmlpage-config',
  imports: [FormsModule, CodemirrorHtmlComponent, GotoBlocklyDirective, PlaceholderInfoDirective, ButtonModule, InputTextModule, SelectModule, ToggleSwitchModule, RadioButtonModule, SelectButtonModule],
  templateUrl: './fusion-htmlpage-config.component.html',
  styleUrl: './fusion-htmlpage-config.component.scss'
})
export class FusionHtmlpageConfigComponent implements OnInit, OnDestroy {
  public safeHtml: SafeHtml = "";
  public subscriptions: SubscriptionLike[] = [];
  public showUrlInput = false;
  public showHtmlEditor = false;
  public editableProxyUrl = '';
  protected get botTypeOptions() {
    return getFusionBotTypeOptions(this.fusionService.loginService.systemConf);
  }

  protected systemLanguageOptions = [
    { label: $localize`:@@fusionconfig-systemlang-de:Deutsch`, value: 'de' },
    { label: $localize`:@@fusionconfig-systemlang-en:Englisch`, value: 'en' },
    { label: $localize`:@@fusionconfig-systemlang-fr:Französisch`, value: 'fr' },
    { label: $localize`:@@fusionconfig-systemlang-es:Spanisch`, value: 'es' },
    { label: $localize`:@@fusionconfig-systemlang-it:Italienisch`, value: 'it' },
    { label: $localize`:@@fusionconfig-systemlang-nl:Niederländisch`, value: 'nl' },
    { label: $localize`:@@fusionconfig-systemlang-nb:Norwegisch`, value: 'nb-no' },
    { label: $localize`:@@fusionconfig-systemlang-fr-be:Französisch (Belgien)`, value: 'fr-be' },
    { label: $localize`:@@fusionconfig-systemlang-nl-be:Niederländisch (Belgien)`, value: 'nl-be' },
    { label: $localize`:@@fusionconfig-systemlang-pl:Polnisch`, value: 'pl' },
    { label: $localize`:@@fusionconfig-systemlang-pt:Portugiesisch`, value: 'pt' },
    { label: $localize`:@@fusionconfig-systemlang-hu:Ungarisch`, value: 'hu' },
    { label: $localize`:@@fusionconfig-systemlang-ro:Rumänisch`, value: 'ro' },
    { label: $localize`:@@fusionconfig-systemlang-tr:Türkisch`, value: 'tr' },
    { label: $localize`:@@fusionconfig-systemlang-fi:Finnisch`, value: 'fi' },
    { label: $localize`:@@fusionconfig-systemlang-sv:Schwedisch`, value: 'sv' },
    { label: $localize`:@@fusionconfig-systemlang-da:Dänisch`, value: 'da' },
    { label: $localize`:@@fusionconfig-systemlang-cs:Tschechisch`, value: 'cs' },
    { label: $localize`:@@fusionconfig-systemlang-el:Griechisch`, value: 'el' }
  ];

  protected viewModeOptions = [
    { label: $localize`:@@fusionconfig-viewmode-split:Split (Side-by-Side)`, value: 'split' },
    { label: $localize`:@@fusionconfig-viewmode-split-reverse:Split Reverse (Side-by-Side)`, value: 'split-reverse' },
    { label: $localize`:@@fusionconfig-viewmode-window:Fenster (Overlay)`, value: 'window' },
    { label: $localize`:@@fusionconfig-viewmode-full:Vollbild`, value: 'full' },
    { label: $localize`:@@fusionconfig-viewmode-float:Floating (Chat-Bubble)`, value: 'float' }
  ];

  constructor(public fusionService: FusionService,
    public rss: RouteShareService,
    public langService: LanguageService,
    public sanitizer: DomSanitizer,
    public toastr: ToastrService,
    protected stageAdmin: StageAdminService,
    public editorItemService: EditorItemsService
  ) { }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => { s.unsubscribe(); });
  }

  public ngOnInit(): void {
    this.subscriptions.push(this.fusionService.onFusionConfigChanged.subscribe(() => {
      this.updateSafeUrlString();
    }));

    this.subscriptions.push(this.fusionService.onFusionConfigDeleted.subscribe(() => {
      this.updateSafeUrlString();
    }));

    this.subscriptions.push(this.fusionService.onFusionConfigCreated.subscribe(() => {
      this.updateSafeUrlString();
    }));

    this.updateSafeUrlString();
  }

  public updateSafeUrlString() {
    if (this.fusionService.selectedHtmlPage) {
      this.safeHtml = this.sanitizer.bypassSecurityTrustResourceUrl(this.fusionService.selectedHtmlPage.metadata.proxyUrl);
      this.editableProxyUrl = this.fusionService.selectedHtmlPage.metadata.proxyUrl;
    }
    return "";
  }

  public async updateProxyUrl(url: string) {
    const trimmed = url.trim();
    if (!trimmed || trimmed === this.fusionService.selectedHtmlPage?.metadata.proxyUrl) return;
    await this.addURL(trimmed);
  }

  public async loadURL(url: string) {
    const result = await this.fusionService.embedInfo(url);
    if (result.status != "ok") {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain:URL ist ungültig`));
      return;
    }

    const html = await this.fusionService.getHtml(url);
    await this.fusionService.updateFusionField('proxyUrl', url);
    await this.fusionService.updateFusionField('useStaticHtml', true);
    await this.fusionService.updateFusionField('staticHtml', html);
  }

  public async addURL(url: string) {
    const result = await this.fusionService.embedInfo(url);
    if (result.status != "ok") {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain:URL ist ungültig`));
      return;
    }

    await this.fusionService.updateFusionField('proxyUrl', url);
    await this.fusionService.updateFusionField('useStaticHtml', false);
  }

  public async resetMode() {
    await this.fusionService.updateFusionField('useStaticHtml', false);
    await this.fusionService.updateFusionField('proxyUrl', '');
    this.showUrlInput = false;
  }

}
